export function simplifyPath(
  points: google.maps.LatLngLiteral[],
  baseTolerance = 0.002, 
  zoom = 12
): google.maps.LatLngLiteral[] {
  if (points.length < 4) return points;

  // Convert lat/lng → approximate meters
  const toXY = (p: google.maps.LatLngLiteral) => ({
    x: p.lng * 111320 * Math.cos((p.lat * Math.PI) / 180),
    y: p.lat * 110540,
  });

  const getDistance = (a: google.maps.LatLngLiteral, b: google.maps.LatLngLiteral) => {
    const A = toXY(a);
    const B = toXY(b);
    return Math.hypot(A.x - B.x, A.y - B.y);
  };

  // 🔧 Tolerance
  const tol = baseTolerance * Math.pow(2, 15 - zoom) * 0.7;

  // 1️⃣ Remove near-duplicates and jitter
  const filtered: google.maps.LatLngLiteral[] = [points[0]];
  for (let i = 1; i < points.length; i++) {
    if (getDistance(points[i], filtered[filtered.length - 1]) > tol / 2) {
      filtered.push(points[i]);
    }
  }

  // 2️⃣ Detect strong direction changes (corner detection)
  const minAngleChange = 30; 
  const corners: google.maps.LatLngLiteral[] = [filtered[0]];
  const toDeg = (rad: number) => (rad * 180) / Math.PI;

  const getAngle = (a: google.maps.LatLngLiteral, b: google.maps.LatLngLiteral) => {
    const A = toXY(a);
    const B = toXY(b);
    return Math.atan2(B.y - A.y, B.x - A.x);
  };

  for (let i = 1; i < filtered.length - 1; i++) {
    const prev = filtered[i - 1];
    const curr = filtered[i];
    const next = filtered[i + 1];
    const ang1 = getAngle(prev, curr);
    const ang2 = getAngle(curr, next);
    let diff = Math.abs(toDeg(ang2 - ang1));
    if (diff > 180) diff = 360 - diff;

    // keep point if direction changes enough OR large gap
    if (diff > minAngleChange || getDistance(prev, curr) > tol * 2) {
      corners.push(curr);
    }
  }
  corners.push(filtered[filtered.length - 1]);

  // 3️⃣ Simplify with Douglas–Peucker
  const simplifyDP = (pts: google.maps.LatLngLiteral[], tolMeters: number): google.maps.LatLngLiteral[] => {
    if (pts.length < 3) return pts;
    const toM = (p: google.maps.LatLngLiteral) => toXY(p);
    const start = toM(pts[0]);
    const end = toM(pts[pts.length - 1]);
    const base = Math.hypot(end.x - start.x, end.y - start.y);
    if (base === 0) return [pts[0]];

    let maxDist = 0;
    let index = 0;

    for (let i = 1; i < pts.length - 1; i++) {
      const c = toM(pts[i]);
      const area = Math.abs(
        (start.x * end.y + end.x * c.y + c.x * start.y -
          end.x * start.y - c.x * end.y - start.x * c.y) / 2
      );
      const dist = (2 * area) / base;
      if (dist > maxDist) {
        index = i;
        maxDist = dist;
      }
    }

    if (maxDist > tolMeters * 4) {
      const left = simplifyDP(pts.slice(0, index + 1), tolMeters);
      const right = simplifyDP(pts.slice(index), tolMeters);
      return [...left.slice(0, -1), ...right];
    } else {
      return [pts[0], pts[pts.length - 1]];
    }
  };

  let simplified = simplifyDP(corners, tol);

  // 4️⃣ Auto-close loop if endpoints are close
  if (getDistance(simplified[0], simplified[simplified.length - 1]) < tol * 2) {
    simplified[simplified.length - 1] = simplified[0];
  }

  if (simplified.length > 14) {
    const step = Math.ceil(simplified.length / 14);
    simplified = simplified.filter((_, i) => i % step === 0);
  }

  return simplified;
}
